import Logo from './Logo';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import React, { useState, useEffect } from 'react';
import { useAdmin } from '../contexts/AdminContext';
import { LockIcon, HeadphonesIcon, ShieldIcon } from './Icons';

const AdminLogin: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    useBodyScrollLock(true);
    const { login } = useAdmin();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Détecter si on est sur la route /agent
    const isAgentRoute = typeof window !== 'undefined' && (
        window.location.pathname.startsWith('/agent') || 
        window.location.hash.startsWith('agent') ||
        window.location.search.includes('agent')
    );

    const [loginMode, setLoginMode] = useState<'agent' | 'admin'>(isAgentRoute ? 'agent' : 'admin');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(false);
        setIsSubmitting(true);
        try {
            const success = await login(username.trim(), password.trim());
            if (success) {
                onClose();
            } else {
                setError(true);
            }
        } catch (err) {
            setError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[99] flex items-center justify-center p-0 sm:p-4 bg-black/90 backdrop-blur-md animate-fade-in">
            <div className="bg-gray-900 border-0 sm:border border-gray-700 p-6 sm:p-8 rounded-none sm:rounded-2xl shadow-2xl w-full h-full sm:h-auto max-h-[100dvh] sm:max-w-md relative flex flex-col justify-center">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-white text-xl font-bold">&times;</button>
                
                {/* Logo Branding */}
                <div className="flex justify-center mb-5">
                    <Logo size="md" showTagline={false} />
                </div>

                {/* Switch Tabs : Conseiller Agent / Admin */}
                <div className="flex bg-black/60 p-1 rounded-xl border border-gray-700/80 mb-6">
                    <button
                        type="button"
                        onClick={() => {
                            setLoginMode('agent');
                            if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/agent')) {
                                window.history.replaceState({}, '', '/agent');
                            }
                        }}
                        className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                            loginMode === 'agent' 
                                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-black shadow-md' 
                                : 'text-gray-400 hover:text-gray-200'
                        }`}
                    >
                        <HeadphonesIcon className="w-4 h-4 inline" />
                        <span>Portail Agent (/agent)</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setLoginMode('admin');
                            if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin')) {
                                window.history.replaceState({}, '', '/admin');
                            }
                        }}
                        className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                            loginMode === 'admin' 
                                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md' 
                                : 'text-gray-400 hover:text-gray-200'
                        }`}
                    >
                        <ShieldIcon className="w-4 h-4 inline" />
                        <span>Administration (/admin)</span>
                    </button>
                </div>

                <div className="flex justify-center mb-4">
                    <div className={`p-3.5 rounded-2xl shadow-lg ${
                        loginMode === 'agent'
                            ? 'bg-gradient-to-tr from-amber-500 to-orange-500 text-black shadow-amber-950/50'
                            : 'bg-gradient-to-tr from-indigo-600 to-purple-600 text-white shadow-indigo-950/50'
                    }`}>
                        {loginMode === 'agent' ? <HeadphonesIcon className="w-7 h-7 text-white" /> : <LockIcon className="w-7 h-7 text-white" />}
                    </div>
                </div>
                
                <h2 className="text-2xl font-black text-white text-center mb-1">
                    {loginMode === 'agent' ? 'Espace Agent Conseiller' : 'Accès Administration'}
                </h2>
                <p className="text-xs text-gray-400 text-center mb-6">
                    {loginMode === 'agent' 
                        ? 'Connectez-vous à votre poste téléphonique et cockpit de chat' 
                        : 'Plateforme de Gestion & Supervision Globale AstroX'}
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-[11px] font-bold text-gray-400 mb-1 uppercase tracking-wider">
                            {loginMode === 'agent' ? 'Identifiant Conseiller' : 'Identifiant Administrateur'}
                        </label>
                        <input 
                            type="text" 
                            placeholder={loginMode === 'agent' ? 'Identifiant conseiller...' : 'Identifiant administrateur...'}
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            autoFocus
                            className="w-full bg-gray-950 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-amber-400 focus:outline-none text-sm font-medium"
                        />
                    </div>
                    <div>
                        <label className="block text-[11px] font-bold text-gray-400 mb-1 uppercase tracking-wider">
                            Mot de passe
                        </label>
                        <input 
                            type="password" 
                            placeholder="Votre mot de passe" 
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="w-full bg-gray-950 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-amber-400 focus:outline-none text-sm font-medium"
                        />
                    </div>

                    {error && (
                        <p className="text-red-400 text-xs text-center bg-red-950/60 border border-red-500/30 p-2.5 rounded-xl animate-fade-in">
                            Identifiant ou mot de passe incorrect. Vérifiez vos accès.
                        </p>
                    )}
                    
                    <button 
                        type="submit"
                        disabled={isSubmitting || !username.trim() || !password.trim()}
                        className={`w-full font-black py-3.5 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 text-sm cursor-pointer disabled:opacity-40 active:scale-98 ${
                            loginMode === 'agent'
                                ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black shadow-amber-950/50'
                                : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-indigo-950/50'
                        }`}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                <span>Vérification des accès...</span>
                            </>
                        ) : (
                            <span>{loginMode === 'agent' ? 'Ouvrir mon Cockpit Agent' : 'Connexion Supervision'}</span>
                        )}
                    </button>
                </form>

                <div className="mt-5 text-center text-[11px] text-gray-500">
                    Lien direct conseiller : <a href="/agent" className="text-amber-400 underline font-bold">astrox.fr/agent</a>
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;
